/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Run plan builder - constructs complete RunPlan from detector results
 */
import type { RunPlan, DetectorResult } from '../types/index.js';
import { detectFixes } from './fix-handler.js';

/**
 * Build a complete RunPlan from a detector result
 */
export const buildRunPlan = async (result: DetectorResult, cwd: string): Promise<RunPlan> => {
  const plan = result.plan;

  if (!plan) {
    throw new Error('Cannot build plan from empty detector result');
  }

  // Build initial plan
  const runPlan: RunPlan = {
    cwd: plan.cwd || cwd,
    pm: plan.pm || null,
    cmd: plan.cmd || '',
    args: plan.args || [],
    env: plan.env,
    reasons: plan.reasons || [],
    fixes: plan.fixes || [],
    confidence: result.confidence,
    detectedType: plan.detectedType,
  };

  // Detect all potential fixes
  const detectedFixes = await detectFixes(runPlan);
  runPlan.fixes = [...(runPlan.fixes || []), ...detectedFixes];

  return runPlan;
};

/**
 * Validate that a RunPlan has all required fields
 */
export const isValidPlan = (plan: RunPlan): boolean => {
  return Boolean(plan.cmd && plan.cwd);
};

